// BUILD-046 correction cycle 1bb88b3e (stage B2a, item N9), laid out as a table row in the layout revision (Layout-3 / Layout-4): the row of the My Tasks tab (the
// Caller's own tasks) in the aligned task table (TaskTable): Task | Priority | Task due date | Actions.
//
// My Tasks emphasises ONE action: Done, a labelled solid green button (one click, one request, no body) joined to its "Done with note" caret (DoneSplit). Everything
// less used - Edit task, Hand off…, History, and for a claimed shared task Reassign… and Return to queue - is in the row's ONE "⋯ More actions" menu (RowMoreMenu,
// through TaskActionControls). A completed row keeps an accessible "Reopen" (the existing restore). The Task cell is the title and, in Comfortable, ONE quiet second
// line of data the task already has: the short requester badge ("By Julie · Oct 18", when a requester is recorded and it is not the Caller), the project, Waiting /
// Blocked, handoff and From <module> (a manual task says "Manual task" in the badge's tooltip instead of a visible "Manual"). Compact is one line: no badge beside
// the title (it has its own "Requested" column when "Show requester" is on). The Priority is a dot AND its
// word; the Task due date is written by dueLabel.ts from the page's existing overdue rule (never recomputed here).
//
// When the Caller summary failed (the routes are off) the legacy page is shown exactly as before in function: the complete button is the legacy
// "Complete" and nothing of the new routes is offered.
import type { WorkItem } from '../../api/client';
import type { WorkTask } from '../../api/workShared';
import type { DoneControlView } from './doneModel';
import type { ActionMe } from './oversightActionsModel';
import { ownerLeftTexts } from './oversightViewModel';
import type { OversightItem } from './oversightViewModel';
import { formatDueDate } from './taskRowModel';
import { NEXT_ACTION_LABEL, NEXT_FOLLOW_UP_DATE_LABEL } from './dateLabels';
// Cycle A (2026-10-21): the row's Overdue label is THE one overdue rule, judged against the page's one Phoenix-day signal.
import { isOverdueTask } from './overdueRule';
import { usePhoenixDay } from './usePhoenixDay';
import type { Density } from './densityStore';
import { useState } from 'react';
import { DoneNotePanel } from './DoneWithNote';
import type { DoneNoteControls } from './DoneWithNote';
import { OriginChip, hasVisibleOrigin } from './OriginChip';
import { RequesterBadge } from './RequesterBadge';
import { comfortableRequesterShown, showRequesterComfortable } from './showRequesterStore';
import { badgeForTask } from './requesterBadgeModel';
import type { MenuEntry } from './RowMoreMenu';
import { TaskActionControls } from './TaskActionControls';
import { TaskHistoryPanel } from './TaskHistoryPanel';
import { ActionsCell, Cell, DueMark, PriorityMark, QuietLine, StatusMark, TaskCell, TaskTableRow } from './TaskTable';
import { jobOf } from './jobDisplayModel';
import { customerOf } from './customerDisplayModel';
import { associationParts } from './CustomerParts';
import { useCustomersInPlay } from './useCustomerColumn';
import { DoneSplit } from './DoneSplit';
// BUILD-046 section C: the visible Update action (where the owner may update an open task), the latest progress note and the follow-up state in Comfortable.
import { editBlockedHint, taskEntryOffers } from './taskActionOffers';
import { followUpState, latestUpdateLine } from './updateTaskModel';
// BUILD-046 G4 (Builder decision 2026-10-22, October 23 addendum, item 2): a row says BOTH conditions - where the follow-up stands and where the deadline stands - in Compact and in Comfortable, and Compact adds the next action beside a follow-up that is due.
import { attentionText, followUpDueOn, nextActionOf } from './attentionModel';
// D1 display cleanup: the title is shown without the "(FICTIONAL)" test marker (labels included); a person's name is First name + last initial (PersonName).
import { descriptionForDisplay, stripTestMarker } from './displayNames';
import { PersonName } from './PersonName';

/**
 * What a Mine row adds when the Caller summary is ready. `done` is null on the legacy page, which keeps its existing complete control. `note` is the
 * "Done with note" control and panel (null on the legacy page, which has no such route).
 */
export interface MineShared {
  simulated: boolean;
  me: (ActionMe & { simulated?: boolean }) | null;
  done: DoneControlView | null;
  onDone: () => void;
  onChanged: () => void;
  note?: DoneNoteControls | null;
}

export interface MineRowProps {
  item: WorkItem;
  density: Density;
  shared: MineShared;
  highlight: boolean;
  onComplete: () => void;
  onEdit: () => void;
  onHandoff: () => void;
  onRestore: () => void;
  /** Opens the Update form for this task (BUILD-046 section C). Absent: no Update action is drawn. */
  onUpdate?: () => void;
}

export function MineRow({ item, density, shared, highlight, onComplete, onEdit, onHandoff, onRestore, onUpdate }: MineRowProps) {
  const task = item as unknown as WorkTask;
  const completed = item.status === 'completed';
  // CHANGED 2026-10-25: while customers are in play, a row with a job and no customer writes "Job: <name>" (associationParts), so a job never reads as a customer.
  const customersInPlay = useCustomersInPlay();
  // Overdue by THE one rule (overdueRule.ts) on the PHOENIX calendar day the page supplies: a task due today reads "Today" and is not overdue; it is overdue from the next Phoenix day.
  const phoenixDay = usePhoenixDay();
  const overdue = isOverdueTask(item, phoenixDay);
  const ownerLeft = shared.me !== null ? ownerLeftTexts(item as unknown as OversightItem) : [];
  const followUp = formatDueDate((item as { followUpDate?: unknown }).followUpDate);
  // BUILD-046 section C: the latest progress note (one quiet line, never a paragraph) and where the follow-up stands against the Phoenix day.
  const latest = latestUpdateLine(task, (date) => formatDueDate(date) ?? date);
  const followState = completed ? 'none' : followUpState((item as { followUpDate?: unknown }).followUpDate, phoenixDay);
  const canUpdate = onUpdate !== undefined && !completed && taskEntryOffers(shared.me, task).update;
  // The conditions as words ("Follow-up today · Overdue"); a completed, ended or restricted task has none. The follow-up and the deadline are independent: a follow-up that is due does not hide an overdue deadline, and does not clear it.
  const attention = attentionText(task, phoenixDay, (date) => formatDueDate(date) ?? date);
  const nextText = followUpDueOn(task, phoenixDay) ? nextActionOf(task) : null;
  // The title as it is SHOWN (and named for assistive technology): the stored title keeps its marker, and Edit still starts from it.
  const shownTitle = stripTestMarker(item.title);
  // D2a: the description as it is SHOWN (the server's default "Handed off from <full name (FICTIONAL)>" is not repeated: the quiet line below already says it with the short name).
  const shownDescription = descriptionForDisplay(item.description, item.handoffFromName);
  const provenance = item.handoffFromName ? (
    <span key="handoff">
      Handed off from <PersonName person={item.handoffFromName} />
    </span>
  ) : item.sourceSystem === 'attention' ? 'From Attention' : null;
  // The quiet second line (Layout-3): only data the task already has. The requester is named only when it is not the Caller. "Updated n ago" is no longer a row label.
  const statusWord = shared.done?.displayStatus ?? item.status;
  // The requester is shown only when one is recorded and it is not the Caller (the rule this line always had). Session edit 4: it is the short badge now
  // ("By Julie · Oct 18", requesterBadgeModel.ts), below the title in Comfortable and in its own column in Compact when "Show requester" is on.
  const showRequester = typeof task.requesterName === 'string' && task.requesterName.trim() !== '' && task.requesterEmployeeId !== shared.me?.employee?.employeeId;
  const badge = badgeForTask(task, showRequester && comfortableRequesterShown(density, showRequesterComfortable.get()));
  // The task's History (read only: what happened, and the notes with who wrote them and when) is offered where the new routes serve it.
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyAvailable = shared.me !== null;
  const note = shared.note ?? null;

  // Layout-4: every secondary action - Edit task, Hand off…, History, and for a claimed shared task Reassign… and Return to queue - is an entry of the ONE "More actions"
  // menu. A completed row keeps its visible Reopen and has History in the menu.
  const historyEntries: MenuEntry[] = historyAvailable ? [{ key: 'history', label: 'History', ariaLabel: `History: ${shownTitle}`, onSelect: () => setHistoryOpen((open) => !open) }] : [];
  // CHANGED 2026-10-22 (BUILD-046 G1, Builder decision requester-edit-authority): Edit task is the REQUESTER's (a task issued to oneself included). The person doing a task someone else issued sees it as an unavailable
  // entry that says why and points at Update; the server refuses the request in any case.
  const editHint = editBlockedHint(shared.me, task);
  const editEntries: MenuEntry[] = taskEntryOffers(shared.me, task).edit
    ? [{ key: 'edit', label: 'Edit task', ariaLabel: `Edit: ${shownTitle}`, onSelect: onEdit }]
    : editHint !== null
      ? [{ key: 'edit', label: 'Edit task', ariaLabel: `Edit task, not available: ${shownTitle}. ${editHint}`, onSelect: () => undefined, disabled: true, hint: editHint }]
      : [];
  const extraEntries: MenuEntry[] = completed
    ? historyEntries
    : [
        ...editEntries,
        { key: 'handoff', label: 'Hand off…', ariaLabel: `Hand off: ${shownTitle}`, onSelect: onHandoff },
        ...(historyAvailable ? [{ key: 'history', label: 'History', ariaLabel: `History: ${shownTitle}`, onSelect: () => setHistoryOpen((open) => !open) }] : []),
      ];

  return (
    <TaskActionControls me={shared.me} row={task} view="my-tasks" onDone={shared.onChanged} extraEntries={extraEntries}>
      {({ trigger, panel }) => (
        <TaskTableRow
          variant="mine"
          id={`work-${item.id}`}
          density={density}
          highlight={highlight}
          requested={badge}
          job={jobOf(task)}
          customer={customerOf(task)}
          below={
            // Increment 1a (D): Compact is ONE tight row, so the owner-left text (the server's, exactly as given) is inline in the row there; panels the person opens are still below.
            (density !== 'compact' && ownerLeft.length > 0) || (note !== null && note.view.open) || (historyOpen && historyAvailable) || panel !== null ? (
              <>
                {density !== 'compact' && ownerLeft.length > 0 && (
                  <ul className="mt-1 text-xs text-amber-800 space-y-0.5" data-testid="owner-left">
                    {ownerLeft.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
                {note !== null && <DoneNotePanel rowId={item.id} title={shownTitle} controls={note} />}
                {historyOpen && historyAvailable && <TaskHistoryPanel taskId={item.id} title={shownTitle} onClose={() => setHistoryOpen(false)} />}
                {panel}
              </>
            ) : undefined
          }
        >
          <TaskCell
            title={item.title}
            density={density}
            titleSuffix={<StatusMark status={statusWord} />}
            badge={badge !== null ? <RequesterBadge badge={badge} /> : null}
            secondary={
              <QuietLine
                parts={[
                  // BUILD-046 J1a: the Customer, then the Job, are the FIRST parts in Comfortable (beside the requester badge); Compact has its own column.
                  ...associationParts(task, density, { customersInPlay }),
                  attention !== null ? <span key="attention" className="font-medium text-amber-800" data-testid="attention-mark">{attention}</span> : null,
                  // Compact draws no detail lines, so a follow-up that is due carries its next action here (Comfortable has it in the detail line).
                  density === 'compact' && nextText !== null ? <span key="next-action" title={`${NEXT_ACTION_LABEL}: ${nextText}`} data-testid="row-next-action">{`Next: ${nextText}`}</span> : null,
                  stripTestMarker(item.projectDisplayName),

                  provenance,
                  shared.me !== null && hasVisibleOrigin(task) ? <OriginChip key="origin" item={task} adapterSimulated={shared.simulated} /> : null,
                  density === 'compact' && ownerLeft.length > 0 ? (
                    <span key="owner-left" className="text-amber-800" title={ownerLeft.join(' ')} data-testid="owner-left">
                      {ownerLeft.join(' ')}
                    </span>
                  ) : null,
                ]}
              />
            }
            detail={
              shownDescription !== '' || item.nextAction || item.waitingOn || followUp !== null || latest !== null ? (
                <div className="space-y-0.5">
                  {/* Increment 1a (D): one quiet line, not a paragraph; the whole description is its hover text. */}
                  {shownDescription !== '' && (
                    <p className="truncate" title={shownDescription}>
                      {shownDescription}
                    </p>
                  )}
                  {latest !== null && (
                    <p className="truncate" title={latest.text} data-testid="latest-update">
                      <span className="font-medium text-gray-700">{latest.label}:</span> {latest.text}
                    </p>
                  )}
                  {(item.nextAction || followUp !== null) && (
                    <p className="flex flex-wrap gap-x-4">
                      {item.nextAction && (
                        <span>
                          <span className="font-medium text-gray-700">{NEXT_ACTION_LABEL}:</span> {item.nextAction}
                        </span>
                      )}
                      {followUp !== null && (
                        <span>
                          <span className="font-medium text-gray-700">{NEXT_FOLLOW_UP_DATE_LABEL}:</span> {followUp}
                          {followState === 'missed' && <span className="ml-1 font-medium text-amber-800" data-testid="follow-up-missed">(missed)</span>}
                          {followState === 'today' && <span className="ml-1 font-medium text-amber-800" data-testid="follow-up-today">(today)</span>}
                        </span>
                      )}
                    </p>
                  )}
                  {item.waitingOn && <p>Waiting on {item.waitingOn}</p>}
                </div>
              ) : undefined
            }
          />
          <Cell stackedLabel="Priority">
            <PriorityMark priority={item.priority} />
          </Cell>
          <Cell stackedLabel="Due">
            <DueMark dueDate={item.dueDate} overdue={overdue} stack={density !== 'compact'} />
          </Cell>
          <ActionsCell>
            {canUpdate && (
              <button
                type="button"
                onClick={onUpdate}
                aria-label={`Update: ${shownTitle}`}
                title="Update: add a progress note and change the status, next action or dates"
                data-testid="update-task"
                className="rounded-md border border-[#2a7fb8] bg-white px-3 py-1.5 text-sm font-medium text-[#1f6fa5] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
              >
                Update
              </button>
            )}
            {completed ? (
              <button
                type="button"
                onClick={onRestore}
                aria-label={`Reopen: ${shownTitle}`}
                title="Reopen: put this task back to the state it had before it was completed"
                className="rounded-md border border-[#2a7fb8] bg-white px-3 py-1.5 text-sm font-medium text-[#1f6fa5] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
              >
                Reopen
              </button>
            ) : shared.done !== null ? (
              <DoneSplit view={shared.done} rowId={item.id} title={shownTitle} onDone={shared.onDone} note={note} compact={density === 'compact'} />
            ) : (
              <button
                type="button"
                onClick={onComplete}
                aria-label={`Complete: ${shownTitle}`}
                className="inline-flex items-center rounded-md border border-green-800 bg-green-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-green-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-green-700"
              >
                Complete
              </button>
            )}
            {trigger}
          </ActionsCell>
        </TaskTableRow>
      )}
    </TaskActionControls>
  );
}
